/**
 * Custom error class for authentication-related errors.
 */
export interface AuthenticationErrorContext {
  subdomain?: string | null
  hasConfigCookie?: boolean
  hasTokens?: boolean
  reason?: 'no_session' | 'no_tokens' | 'expired' | 'invalid_subdomain' | 'missing_config'
}

export class AuthenticationError extends Error {
  public readonly context: AuthenticationErrorContext

  constructor(message: string, context: AuthenticationErrorContext = {}) {
    super(message)
    this.name = 'AuthenticationError'
    this.context = context

    // Capture stack trace properly
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AuthenticationError)
    }
  }

  /**
   * Get a user-friendly message based on the error context
   */
  getUserMessage(): string {
    switch (this.context.reason) {
      case 'no_session':
        return 'Your session has expired. Please log in again.'
      case 'no_tokens':
        return 'Authentication tokens are missing. Please log in.'
      case 'expired':
        return 'Your session has expired. Please log in again.'
      case 'invalid_subdomain':
        return 'Unable to determine your account. Please try again.'
      case 'missing_config':
        return 'Account configuration not found. Please contact support.'
      default:
        return 'Please log in to continue.'
    }
  }
}

/**
 * Check if an error is an authentication error.
 * Works across the server/client boundary where the class type may be lost.
 */
export function isAuthenticationError(error: Error): boolean {
  return (
    error.name === 'AuthenticationError' ||
    error.message?.includes('Authentication required') ||
    error.message?.includes('No valid session')
  )
}
